import { useEffect, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';
import { useAtom } from 'jotai';

import { getPaymentPage } from 'api/rest/payments/getPaymentPage';
import { Acquirer } from 'data/merchant/models/acquirer';
import { notifyAboutClientPaymentAtom } from 'data/payments/atoms';
import { usePollPayment } from 'data/payments/hooks';
import {
  DepositCredentials,
  DepositMethod,
  DepositStatus,
} from 'data/payments/models';
import {
  DepositPaymentPage,
  isPageRendererRequested,
} from 'data/payments/paymentPage';
import { useUploadDepositClientFiles } from 'data/traders/deposits/hooks/useUploadDepositClientFiles';
import {
  AnalyticsEvents,
  identify,
  inputEventMap,
  InputType,
  trackEvent,
} from 'utils/analytics';
import { calculateTimeLeft } from 'utils/calculateTimeLeft';
import { getSecondsLeft } from 'utils/getSecondsLeft';

import { PaymentCredentialsDefault } from './PaymentCredentialsDefault';
import { PaymentCredentialsFromPage } from './PaymentCredentialsFromPage';

export type PaymentCredentialsStyle = 'default' | '1xbet' | 'riobet';

export interface PaymentCredentialsBaseProps {
  id: string;
  acquirer: Acquirer;
  method: DepositMethod | null;
  amount: string;
  currency: string;
  credentials: DepositCredentials;
  failureRedirectLink: string | null;
  fromBank?: string | null;
  status?: DepositStatus;
  timeLeft: string;
  paid?: boolean;
  onPaid: () => void;
  onCancel: () => Promise<void>;
  onInputCopied: (inputName: InputType) => void;
  onUploadClick: () => void;
  onUploadComplete: (formData: FormData) => Promise<void>;
  isUploadLoading: boolean;
  uploadModalIsOpen: boolean;
  uploadModalOnClose: () => void;
}

interface PaymentCredentialsProps {
  id: string;
  acquirer: Acquirer;
  method: DepositMethod | null;
  amount: string;
  currency: string;
  expiryDate: Date;
  credentials: DepositCredentials;
  failureRedirectLink: string | null;
  crossborderAmount: string | null;
  crossborderCurrency: string | null;
  customerId?: string | null;
  style?: PaymentCredentialsStyle;
  fromBank?: string | null;
  status?: DepositStatus;
  timeLeft?: string;
  showChatwoot?: boolean;
}

export function PaymentCredentials({
  id,
  acquirer,
  method,
  amount,
  currency,
  expiryDate,
  credentials,
  failureRedirectLink,
  customerId,
  style = 'default',
  fromBank,
  status,
  timeLeft: initialTimeLeft,
  crossborderAmount,
  crossborderCurrency,
  showChatwoot,
}: PaymentCredentialsProps) {
  const [timeLeft, setTimeLeft] = useState(
    initialTimeLeft || calculateTimeLeft(expiryDate),
  );
  const [isTimeAlreadyExpired, setIsTimeAlreadyExpired] = useState(
    getSecondsLeft(expiryDate) <= 0,
  );

  const [_, notify] = useAtom(notifyAboutClientPaymentAtom);
  const storageKey = `payment-paid-${id}`;

  const [paid, setPaid] = useState(
    () => localStorage.getItem(storageKey) === 'true',
  );
  usePollPayment(id);

  // The page from the method's data, opt-in with `?renderer=page`; without a
  // page, or when it fails to load, the old page is drawn
  const [pageData, setPageData] = useState<
    DepositPaymentPage | null | undefined
  >(() => (isPageRendererRequested() ? undefined : null));
  useEffect(() => {
    if (pageData !== undefined) return;
    getPaymentPage(id).then(setPageData, () => setPageData(null));
  }, [id, pageData]);
  const { isUploadLoading, onUploadComplete } = useUploadDepositClientFiles(id);

  const uploadDisclosure = useDisclosure();

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(expiryDate));
      const newTimeLeft = getSecondsLeft(expiryDate);

      if (newTimeLeft <= 0 && !isTimeAlreadyExpired) {
        clearInterval(interval);

        trackEvent(AnalyticsEvents.PAYMENT_TIMER_EXPIRED, {
          order_id: id,
          payment_method: method ?? '',
          amount_base: amount,
        });
        setIsTimeAlreadyExpired(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryDate, id, method, amount, isTimeAlreadyExpired]);

  useEffect(() => {
    if (customerId) {
      identify({ user: { id: customerId } });
    }
  }, [customerId]);

  useEffect(() => {
    if (paid && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, 'true');
    }
  }, [paid, storageKey]);

  const handleUploadClick = () => {
    trackEvent(AnalyticsEvents.PAYMENT_UPLOAD_BILL_CLICKED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
    });
  };

  const handleUploadComplete = async (formData: FormData) => {
    await onUploadComplete(formData);
    await trackEvent(AnalyticsEvents.PAYMENT_BILL_UPLOADED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
      amount_local: crossborderAmount ?? '',
      local_currency: crossborderCurrency ?? '',
    });
    notify({ depositId: id });
    if (!paid) {
      setPaid(true);
    }
  };

  const handlePaid = () => {
    uploadDisclosure.onOpen();

    trackEvent(AnalyticsEvents.PAYMENT_CONFIRMED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
    });
  };

  const handleCancel = async () => {
    await trackEvent(AnalyticsEvents.PAYMENT_CANCELLED, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
    });
  };

  const handleInputCopied = (inputName: InputType) => {
    const eventName = inputEventMap[inputName];

    trackEvent(eventName, {
      order_id: id,
      payment_method: method ?? '',
      amount_base: amount,
      amount_local: crossborderAmount ?? '',
      local_currency: crossborderCurrency ?? '',
    });
  };

  if (pageData === undefined) return null;

  const props = {
    id,
    acquirer,
    method,
    amount,
    currency,
    credentials,
    failureRedirectLink,
    fromBank,
    status,
    timeLeft,
    paid,
    onPaid: handlePaid,
    onCancel: handleCancel,
    onInputCopied: handleInputCopied,
    onUploadClick: handleUploadClick,
    onUploadComplete: handleUploadComplete,
    isUploadLoading,
    uploadModalIsOpen: uploadDisclosure.isOpen,
    uploadModalOnClose: uploadDisclosure.onClose,
    crossborderAmount,
    crossborderCurrency,
    showChatwoot,
  };

  if (pageData?.page)
    return (
      <PaymentCredentialsFromPage
        {...props}
        data={{ ...pageData, page: pageData.page }}
      />
    );

  return (
    <PaymentCredentialsDefault
      id={id}
      acquirer={acquirer}
      method={method}
      amount={amount}
      currency={currency}
      credentials={credentials}
      failureRedirectLink={failureRedirectLink}
      fromBank={fromBank}
      status={status}
      timeLeft={timeLeft}
      paid={paid}
      onPaid={handlePaid}
      onCancel={handleCancel}
      onInputCopied={handleInputCopied}
      onUploadClick={handleUploadClick}
      onUploadComplete={handleUploadComplete}
      isUploadLoading={isUploadLoading}
      uploadModalIsOpen={uploadDisclosure.isOpen}
      uploadModalOnClose={uploadDisclosure.onClose}
      style={style}
      crossborderAmount={crossborderAmount}
      crossborderCurrency={crossborderCurrency}
      showChatwoot={showChatwoot}
    />
  );
}
